import { useEffect, useState } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { useAdminOrg } from '@/hooks/useAdminOrg';
import { useAuth } from '@/contexts/AuthContext';
import { useToast } from '@/hooks/use-toast';
import { Button } from '@/components/ui/button';
import { Switch } from '@/components/ui/switch';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import { Banknote, ExternalLink, Link2, Loader2, Save, Wallet, ShieldCheck, Percent, PoundSterling } from 'lucide-react';
import {
  AdminPage,
  PageHeader,
  Panel,
  SettingRow,
  StatusChip,
  adminButton,
  useAdminPageTitle,
} from '@/components/admin/ui';
import { cn } from '@/lib/utils';
import { DEFAULT_PAYMENT_METHODS, PaymentMethodSettings } from '@/hooks/useOrgSettings';
import { DEFAULT_PAYMENT_POLICY, PaymentPolicy } from '@/lib/paymentPolicy';
import { usePaymentsAdminData, useSavePayments } from '@/hooks/usePaymentsAdmin';
import { canTakeCardPayments, useStripeConnectStatus } from '@/hooks/useStripeConnect';
import { StripeConnectPanel } from '@/components/admin/payments/StripeConnectPanel';
import { buildPaypalMeUrl, normalisePaypalMeHandle } from '@/lib/paypal';
import { resolveCashEnabled } from '@/lib/bookingPayment';

const paypalFormSchema = z
  .object({
    enabled: z.boolean(),
    paypalMe: z.string().trim().max(200, 'That looks too long for a PayPal.me link'),
  })
  .superRefine((val, ctx) => {
    if (val.paypalMe && !normalisePaypalMeHandle(val.paypalMe)) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['paypalMe'],
        message: 'Enter your PayPal.me name, e.g. maidmadness (letters and numbers, up to 20).',
      });
    } else if (val.enabled && !val.paypalMe) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['paypalMe'],
        message: 'Add your PayPal.me name to switch PayPal on.',
      });
    }
  });
type PaypalFormValues = z.infer<typeof paypalFormSchema>;

/** Radio "card" option: whole tile is the label; highlights when checked. */
const optionCard =
  'flex cursor-pointer items-start gap-3 rounded-xl border border-border bg-card p-3.5 transition-colors hover:bg-muted/50 has-[[data-state=checked]]:border-primary/40 has-[[data-state=checked]]:bg-secondary/60 has-[[data-state=checked]]:shadow-[var(--adm-shadow-xs)]';

const PaymentsAdmin = () => {
  useAdminPageTitle('Payment Methods');
  const { orgId, loading: orgLoading, canWrite } = useAdminOrg();
  const { isOrgOwner, isPlatformAdmin } = useAuth();
  const { toast } = useToast();
  const [methods, setMethods] = useState<PaymentMethodSettings>(DEFAULT_PAYMENT_METHODS);
  const [policy, setPolicy] = useState<PaymentPolicy>(DEFAULT_PAYMENT_POLICY);
  const [companyInfoId, setCompanyInfoId] = useState<string | null>(null);

  const { data, isLoading } = usePaymentsAdminData(orgId, orgLoading);
  const saveMutation = useSavePayments(orgId);
  const stripeStatus = useStripeConnectStatus(orgId);
  const paypalForm = useForm<PaypalFormValues>({
    resolver: zodResolver(paypalFormSchema),
    mode: 'onChange',
    defaultValues: { enabled: false, paypalMe: '' },
  });
  const paypalValues = paypalForm.watch();
  const paypalHandle = normalisePaypalMeHandle(paypalValues.paypalMe);

  // Seed local form state when query data arrives
  useEffect(() => {
    if (!data) return;
    setCompanyInfoId(data.companyInfoId || null);
    setMethods(data.methods);
    setPolicy(data.policy);
    paypalForm.reset({ enabled: data.methods.paypal.enabled, paypalMe: data.methods.paypal.paypal_me ?? '' });
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data]);

  // Surface query errors as toasts
  const { isError, error } = usePaymentsAdminData(orgId, orgLoading);
  useEffect(() => {
    if (isError && error) {
      toast({ title: 'Error', description: (error as Error).message, variant: 'destructive' });
    }
  }, [isError, error, toast]);

  const canSave = canWrite && (isOrgOwner || isPlatformAdmin);
  // Cash can only go off when customers have another way to pay.
  const cardReady = Boolean(stripeStatus.data && canTakeCardPayments(stripeStatus.data) && methods.stripe.checkout_enabled);
  const paypalReady = Boolean(paypalValues.enabled && paypalHandle);
  const cashCanBeTurnedOff = cardReady || paypalReady;
  const cashEnabled = resolveCashEnabled({ requested: methods.cash_in_person.enabled, cardReady, paypalReady });
  const saving = saveMutation.isPending;

  const handleSave = async () => {
    if (!companyInfoId || !canSave) return;
    const valid = await paypalForm.trigger();
    if (!valid) {
      toast({
        title: 'Check your PayPal details',
        description: paypalForm.formState.errors.paypalMe?.message ?? 'PayPal.me name is not valid.',
        variant: 'destructive',
      });
      return;
    }
    const { enabled, paypalMe } = paypalForm.getValues();
    const handle = normalisePaypalMeHandle(paypalMe);
    const nextMethods: PaymentMethodSettings = {
      ...methods,
      paypal: {
        ...methods.paypal,
        enabled: Boolean(enabled && handle),
        available: Boolean(enabled && handle),
        status: enabled && handle ? 'active' : 'not_set_up',
        paypal_me: handle ?? '',
      },
    };
    try {
      await saveMutation.mutateAsync({
        companyInfoId,
        methods: nextMethods,
        policy,
        // Only force off when we positively know the account can't charge.
        checkoutAllowed: stripeStatus.data ? canTakeCardPayments(stripeStatus.data) : undefined,
      });
      toast({ title: 'Saved', description: 'Payment methods updated' });
    } catch (err) {
      toast({ title: 'Error', description: (err as Error).message, variant: 'destructive' });
    }
  };

  if (isLoading) {
    return (
      <AdminPage width="narrow">
        <div className="flex items-center justify-center p-8 text-[13px] text-muted-foreground">
          <Loader2 className="mr-2 h-4 w-4 animate-spin" /> Loading payment settings…
        </div>
      </AdminPage>
    );
  }

  return (
    <AdminPage width="narrow" className="adm-form">
      <PageHeader description="Choose how customers can pay you: cards through your own Stripe account, PayPal.me, or cash in person. Card customers pay when they book, so no-shows don’t cost you." />

      <StripeConnectPanel
        orgId={orgId}
        canManage={canSave}
        checkoutEnabled={methods.stripe.checkout_enabled ?? false}
        onCheckoutEnabledChange={(checked) =>
          setMethods((prev) => ({ ...prev, stripe: { ...prev.stripe, checkout_enabled: checked } }))
        }
      />

      <Panel
        title={
          <span className="flex flex-wrap items-center gap-2">
            PayPal
            {paypalValues.enabled && paypalHandle ? (
              <StatusChip tone="green">Active</StatusChip>
            ) : (
              <StatusChip tone="grey">Not set up</StatusChip>
            )}
          </span>
        }
        icon={<Wallet className="h-4 w-4 text-muted-foreground" />}
        description="Customers pay you directly through PayPal; we never touch the money. Amounts are filled in for invoices."
        bodyClassName="pt-3 md:pt-3"
      >
        <div className="space-y-4">
          <div className="space-y-1.5">
            <Label htmlFor="paypal-me">PayPal.me name</Label>
            <Input
              id="paypal-me"
              placeholder="maidmadness or paypal.me/maidmadness"
              autoComplete="off"
              autoCapitalize="none"
              spellCheck={false}
              aria-invalid={Boolean(paypalForm.formState.errors.paypalMe)}
              disabled={!canSave}
              {...paypalForm.register('paypalMe')}
            />
            {paypalForm.formState.errors.paypalMe ? (
              <p role="alert" className="text-[12px] font-medium text-destructive">
                {paypalForm.formState.errors.paypalMe.message}
              </p>
            ) : (
              <p className="adm-hint">Paste your PayPal.me link or just the name after paypal.me/.</p>
            )}
          </div>

          {paypalHandle && (
            <div className="flex flex-wrap items-center justify-between gap-3 rounded-xl border border-border bg-muted/40 p-3.5">
              <div className="min-w-0">
                <p className="text-[12px] text-muted-foreground">Your payment link</p>
                <p className="truncate font-mono text-[12.5px] text-foreground" data-testid="paypal-preview">
                  {buildPaypalMeUrl(paypalHandle)}
                </p>
              </div>
              <Button asChild size="sm" className={adminButton.outline}>
                <a href={buildPaypalMeUrl(paypalHandle) ?? undefined} target="_blank" rel="noopener noreferrer">
                  Test link <ExternalLink />
                </a>
              </Button>
            </div>
          )}

          <div className="flex items-start justify-between gap-4">
            <div className="min-w-0 space-y-0.5">
              <Label htmlFor="paypal-enabled">Offer PayPal to customers</Label>
              <p className="adm-hint">Shown as a payment option when booking, and as a "Pay with PayPal" link on invoices.</p>
            </div>
            <Controller
              control={paypalForm.control}
              name="enabled"
              render={({ field }) => (
                <Switch
                  id="paypal-enabled"
                  checked={field.value}
                  onCheckedChange={(checked) => {
                    field.onChange(checked);
                    // The "needs a handle" rule is reported on the handle field.
                    void paypalForm.trigger('paypalMe');
                  }}
                  disabled={!canSave}
                />
              )}
            />
          </div>
        </div>
      </Panel>

      <Panel
        title="Other ways to get paid"
        icon={<Banknote className="h-4 w-4 text-muted-foreground" />}
        description="Cash, and advanced options if you already use a static Stripe link."
        bodyClassName="pt-1 md:pt-1"
      >
        <div className="adm-divide">
          <SettingRow
            className="bg-card"
            icon={<Banknote />}
            iconTone="green"
            title={
              <span className="flex flex-wrap items-center gap-2">
                Cash in person
                {cashEnabled ? <StatusChip tone="green">Enabled</StatusChip> : <StatusChip tone="grey">Off</StatusChip>}
              </span>
            }
            description={
              cashCanBeTurnedOff
                ? 'Customer pays the cleaner on the day. Customers who choose cash can book without paying upfront, so that’s your call. Turn it off and card customers must pay to book.'
                : 'Customer pays the cleaner on the day, so they can book without paying upfront. You can turn this off once card payments or PayPal are set up.'
            }
            control={
              <Switch
                checked={cashEnabled}
                disabled={!canSave || !cashCanBeTurnedOff}
                onCheckedChange={(checked) =>
                  setMethods((prev) => ({ ...prev, cash_in_person: { ...prev.cash_in_person, enabled: checked } }))
                }
                aria-label="Cash in person"
              />
            }
          />

          <SettingRow
            className="bg-card"
            icon={<Link2 />}
            iconTone="violet"
            title="Stripe Payment Link (advanced)"
            description="Already have a Stripe Payment Link? Paste it here and it is included in acceptance emails. Not needed if you connect Stripe above."
          >
            <div className="space-y-1.5">
              <Label htmlFor="stripe-payment-link">Stripe Payment Link</Label>
              <Input
                id="stripe-payment-link"
                type="url"
                inputMode="url"
                placeholder="https://buy.stripe.com/..."
                value={methods.stripe.payment_link ?? ''}
                onChange={(e) =>
                  setMethods((prev) => ({ ...prev, stripe: { ...prev.stripe, payment_link: e.target.value } }))
                }
                disabled={!canSave}
              />
            </div>
          </SettingRow>
        </div>
      </Panel>

      {/* Payment Policy */}
      <Panel
        title="Payment policy"
        icon={<ShieldCheck className="h-4 w-4 text-muted-foreground" />}
        description="Card customers pay this when they book, in full or a deposit, so a no-show doesn’t cost you. Set that here, plus your cancellation and refund terms."
      >
        <div className="space-y-6">
          {/* Payment type */}
          <div className="space-y-2">
            <Label>Charge type</Label>
            <RadioGroup
              value={policy.payment_type}
              onValueChange={(v) =>
                setPolicy((prev) => ({ ...prev, payment_type: v as PaymentPolicy['payment_type'] }))
              }
              disabled={!canSave}
              className="grid gap-2 sm:grid-cols-2"
            >
              <label className={optionCard}>
                <RadioGroupItem value="full" id="pt-full" className="mt-0.5" />
                <div className="flex-1">
                  <div className="text-[13.5px] font-semibold text-foreground">Full payment</div>
                  <div className="mt-0.5 text-[12px] font-normal text-muted-foreground">Customers pay the full amount at checkout.</div>
                </div>
              </label>
              <label className={optionCard}>
                <RadioGroupItem value="deposit" id="pt-deposit" className="mt-0.5" />
                <div className="flex-1">
                  <div className="text-[13.5px] font-semibold text-foreground">Deposit</div>
                  <div className="mt-0.5 text-[12px] font-normal text-muted-foreground">Customers pay a deposit now and the balance later.</div>
                </div>
              </label>
            </RadioGroup>
          </div>

          {/* Deposit settings (only when deposit is selected) */}
          {policy.payment_type === 'deposit' && (
            <div className="grid gap-4 rounded-xl border border-border bg-muted/30 p-4 sm:grid-cols-2">
              <div className="space-y-2">
                <Label>Deposit type</Label>
                <RadioGroup
                  value={policy.deposit_type}
                  onValueChange={(v) =>
                    setPolicy((prev) => ({ ...prev, deposit_type: v as PaymentPolicy['deposit_type'] }))
                  }
                  disabled={!canSave}
                  className="adm-segmented w-fit"
                >
                  {([
                    ['percentage', 'dt-pct', 'Percentage', Percent],
                    ['fixed', 'dt-fixed', 'Fixed amount', PoundSterling],
                  ] as const).map(([value, id, text, Icon]) => (
                    <label
                      key={value}
                      className={cn(
                        'flex h-[28px] cursor-pointer items-center gap-1.5 rounded-[7px] px-3 text-[12.5px] font-medium text-muted-foreground transition-colors',
                        'has-[[data-state=checked]]:bg-card has-[[data-state=checked]]:text-foreground has-[[data-state=checked]]:shadow-[var(--adm-shadow-sm)]',
                      )}
                    >
                      <RadioGroupItem value={value} id={id} className="sr-only" />
                      <Icon className="h-3.5 w-3.5" />
                      <span>{text}</span>
                    </label>
                  ))}
                </RadioGroup>
              </div>
              <div className="space-y-2">
                <Label htmlFor="deposit-value">
                  Deposit value{policy.deposit_type === 'percentage' ? ' (%)' : ' (amount)'}
                </Label>
                <div className="flex items-center gap-2">
                  <Input
                    id="deposit-value"
                    type="number"
                    min="0"
                    step={policy.deposit_type === 'percentage' ? '1' : '0.01'}
                    max={policy.deposit_type === 'percentage' ? '100' : undefined}
                    value={policy.deposit_value}
                    onChange={(e) =>
                      setPolicy((prev) => ({ ...prev, deposit_value: Number(e.target.value) || 0 }))
                    }
                    disabled={!canSave}
                    className="w-32"
                  />
                  <span className="text-[12.5px] text-muted-foreground">
                    {policy.deposit_type === 'percentage' ? '%' : 'currency units'}
                  </span>
                </div>
              </div>
            </div>
          )}

          {/* Cancellation policy */}
          <div className="space-y-1.5">
            <Label htmlFor="cancellation-policy">Cancellation policy</Label>
            <Textarea
              id="cancellation-policy"
              rows={3}
              placeholder="e.g. Cancellations made at least 24 hours before the appointment are accepted free of charge."
              value={policy.cancellation_policy}
              onChange={(e) =>
                setPolicy((prev) => ({ ...prev, cancellation_policy: e.target.value }))
              }
              disabled={!canSave}
            />
            <p className="adm-hint">
              This text is shown to customers on the booking page before they confirm.
            </p>
          </div>

          {/* Refund window */}
          <div className="space-y-1.5">
            <Label htmlFor="refund-window">Refund window (hours)</Label>
            <div className="flex items-center gap-2">
              <Input
                id="refund-window"
                type="number"
                min="0"
                step="1"
                value={policy.refund_window_hours}
                onChange={(e) =>
                  setPolicy((prev) => ({ ...prev, refund_window_hours: Math.max(0, Math.round(Number(e.target.value) || 0)) }))
                }
                disabled={!canSave}
                className="w-32"
              />
              <span className="text-[12.5px] text-muted-foreground">hours before appointment</span>
            </div>
            <p className="adm-hint">
              Full refund is offered when a customer cancels at least this many hours ahead. Set to 0 to disable.
            </p>
          </div>

          {/* Refund policy */}
          <div className="space-y-1.5">
            <Label htmlFor="refund-policy">Refund policy</Label>
            <Textarea
              id="refund-policy"
              rows={3}
              placeholder="e.g. A full refund is available if cancelled more than 24 hours in advance. No refunds within 24 hours."
              value={policy.refund_policy}
              onChange={(e) =>
                setPolicy((prev) => ({ ...prev, refund_policy: e.target.value }))
              }
              disabled={!canSave}
            />
          </div>
        </div>
      </Panel>

      <div className="adm-panel sticky bottom-3 z-10 flex items-center justify-end gap-3 px-3 py-2.5 shadow-[var(--adm-shadow-md)] sm:bottom-4 sm:justify-between sm:px-4 md:px-5">
        <p className="hidden text-[12.5px] text-muted-foreground sm:block">
          {canSave ? 'Changes apply to new bookings and invoices.' : 'Only the organisation owner can change payment settings.'}
        </p>
        <Button size="sm" className={cn(adminButton.primary, 'w-full sm:w-auto')} onClick={handleSave} disabled={!canSave || saving}>
          {saving ? <Loader2 className="animate-spin" /> : <Save />}
          Save changes
        </Button>
      </div>
    </AdminPage>
  );
};

export default PaymentsAdmin;
